import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '../..';
import IConnectToTeam from '../../../model/IConnectToTeam';
import { fetchConnectToTeams, fetchConnectToTeamsForReporting } from './helpAPI';
import { LoadStatus } from '../../../model';
import { createAppThunk } from '../../thunks';

export interface HelpState {
    connectToTeamsStatus: LoadStatus;
    connectToTeams: IConnectToTeam[];
    selectedConnectToTeam?: IConnectToTeam;
    message: string;
    isSubmitting?: boolean;
    simulateFailedConnection?: boolean;
    reportingTeamsStatus: LoadStatus;
    reportingTeams: IConnectToTeam[];
}

const initialState: HelpState = {
    connectToTeamsStatus: 'loading',
    message: '',
    connectToTeams: [],
    reportingTeamsStatus: 'idle',
    reportingTeams: [],
};

export const getConnectToTeams = createAppThunk(
    'help/fetchConnectToTeams',
    async () => {
        const response = await fetchConnectToTeams();
        return response.data;
    }
);

export const getConnectToTeamsForReporting = createAppThunk(
    'help/fetchConnectToTeamsForReporting',
    async () => {
        const response = await fetchConnectToTeamsForReporting();
        return response.data;
    }
);

export const helpSlice = createSlice({
    name: 'help',
    initialState,
    reducers: {
        selectTeam: (state, action: PayloadAction<string | undefined>) => {
            if (!action.payload) {
                state.selectedConnectToTeam = undefined;
            } else {
                const team = state.connectToTeams.find(
                    (x) => x.key === action.payload
                );
                state.selectedConnectToTeam = team;
            }
        },
        updateMessage: (state, action: PayloadAction<string>) => {
            state.message = action.payload;
        },
        setIsSubmitting: (state, action: PayloadAction<boolean>) => {
            state.isSubmitting = action.payload;
        },
        setSimulateFailedConnection: (
            state,
            action: PayloadAction<boolean>
        ) => {
            state.simulateFailedConnection = action.payload;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(getConnectToTeams.pending, (state) => {
                state.connectToTeamsStatus = 'loading';
            })
            .addCase(getConnectToTeams.fulfilled, (state, action) => {
                state.connectToTeamsStatus = 'idle';
                state.connectToTeams = action.payload;
                if (action.payload.length > 0) {
                    state.selectedConnectToTeam = action.payload[0];
                }
            })
            .addCase(getConnectToTeams.rejected, (state) => {
                state.connectToTeamsStatus = 'failed';
            })
            .addCase(getConnectToTeamsForReporting.pending, (state) => {
                state.reportingTeamsStatus = 'loading';
            })
            .addCase(getConnectToTeamsForReporting.fulfilled, (state, action) => {
                state.reportingTeamsStatus = 'idle';
                state.reportingTeams = action.payload;
            })
            .addCase(getConnectToTeamsForReporting.rejected, (state) => {
                state.reportingTeamsStatus = 'failed';
            });
    },
});

export const {
    selectTeam,
    updateMessage,
    setIsSubmitting,
    setSimulateFailedConnection,
} = helpSlice.actions;

export const selectHelp = (state: RootState) => state.help;

export default helpSlice.reducer;
